/**
 * 画面詳細のコンポーネントの表と、表・右パネル・タブで共有する小さな部品 — ⓘ、空の印、コンポーネントの
 * 欄を文字にする関数。
 *
 * **呼び名は見本の画面仕様書の列名をそのまま使う**（コンポーネント名 ・ 種別 ・ 表示条件 ・ 変数部の表示
 * ロジック ・ 操作結果）。言い換えを作らない（Dhik 2026-09-29「画面詳細の部品はコンポーネント名、押すとは
 * 操作結果にしてほしい。謎日本語使うな。」）。
 *
 * **表はコンポーネントどうしを見比べる 3 列（コンポーネント名 ／ 種別 ／ 操作結果）だけにする。** 残りの
 * 項目（表示条件・変数部の表示ロジック）は行を押すと開く右パネルが持つ（Dhik 2026-09-29「表だとゴチャつく
 * から押下したときのサイドバーの情報といい感じに分散させてほしい」）。
 *
 * **表のセルにリンクを置かない。** 行を押すと右パネルが開き、業務・遷移先の画面への
 * 入口は右パネルが持つ（UI の合意 2026-09-28「表 1 つ ＋ 右パネル」）。セルにもリンクを
 * 置くと、行を押したつもりでリンクを踏み、右パネルを開かずに別の画面へ移る。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面詳細のコンポーネントの表（コンポーネント名 ／ 種別 ／ 操作結果）と、ⓘ・空の印・コンポーネントの欄の文字を共有する
 * @graph-connects none
 */
import type { ScreenComponentRow } from '@cortex/domain-spec-console';
import { useTranslation } from 'react-i18next';

import { ElementTermLink } from '../components/spec/TermText.js';
import { displayLabel, type LabelTranslate } from '../components/spec/UntranslatedBadge.js';
import { RowOpenButton, selectableRowProps } from './selectable-row.js';

/** 表のセルの共通 class。 @graph-connects none */
export const CELL_CLASS = 'border border-[var(--color-border)] px-3 py-2 text-left align-top';

/** 表の見出しセルの共通 class。 @graph-connects none */
export const HEAD_CELL_CLASS = `${CELL_CLASS} text-xs font-bold text-[var(--color-text-muted)]`;

/** 値が無いセルに置く印。欄が空であることだけを言い、理由の札は付けない。 @graph-connects none */
export const DASH = '—';

/**
 * ⓘ 1 つ。知らないと読み違えるものにだけ付ける（1 画面 3 つまで）。
 *
 * 中身は `title`（指すと出る）と `aria-label`（読み上げ）に同じ文を入れる。**`<button>` に
 * する**のは、キーボードで辿れて、支援技術にも「フォーカスして読めるもの」として伝わる
 * ためである（`role="img"` に `tabIndex` を付けると、フォーカスは来るが操作できる要素とは
 * 読まれない）。見えている「i」は飾りなので読み上げから外す。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 読み違えやすい札・列見出しの横に、指す・フォーカスで説明が出る ⓘ をボタンとして描く。
 * @graph-connects none
 */
export function InfoMark({ text }: { text: string }) {
  return (
    <button
      type="button"
      title={text}
      aria-label={text}
      data-info-mark
      className="ml-1 inline-grid h-3.5 w-3.5 cursor-help place-items-center rounded-full border border-[var(--color-text-muted)] align-middle text-[9px] leading-none font-bold text-[var(--color-text-muted)] focus-visible:outline-2 focus-visible:outline-[var(--color-border-focus)]"
    >
      <span aria-hidden="true">i</span>
    </button>
  );
}

/**
 * コンポーネントの種別を文字にする。種別が無ければ `null`。
 *
 * @param component コンポーネント 1 件
 * @param translate 表示言語への翻訳
 * @returns 種別の名前。無ければ null
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business コンポーネントの種別を表示言語の名前にし、種別の決まっていないコンポーネントは空にする。
 * @graph-connects none
 */
export function componentKindText(
  component: ScreenComponentRow,
  translate: LabelTranslate,
): string | null {
  return component.kind === null
    ? null
    : translate(`businessLogic.componentKinds.${component.kind}`);
}

/** 差し込みつきの翻訳（i18next の `t` と同じ形）。 @graph-connects none */
export type ParamTranslate = (key: string, params?: Record<string, unknown>) => string;

/**
 * コンポーネント名の型紙の中で、用語が入る位置の印。型紙を訳してからこの印で割り、間に用語を差し込む。
 *
 * 語順は言語で違う（日本語・英語は「用語 種別」、ベトナム語は「種別 用語」）。前後の文字を
 * 画面に書くと語順を画面が決めることになるので、語順は訳の型紙に持たせる。
 * @graph-connects none
 */
const SUBJECT_SLOT = '\u0000';

/** `ComponentName` の props。 @graph-connects none */
interface ComponentNameProps {
  /** コンポーネント 1 件。 */
  component: ScreenComponentRow;
  /**
   * 用語の部分を用語詳細へのリンクにするか。表のコンポーネント名は行を開くボタンの中に居るので
   * リンクにできない（`<a>` を `<button>` に入れられない）。右パネルの見出しでリンクにする。
   */
  link: boolean;
}

/**
 * コンポーネント名を「<用語><種別>」で描く（例: 会員のニックネーム ＋ ボタン）。
 *
 * コンポーネントの表示名はもともと用語と種別から組まれているが、組んだ後の文字列からは用語の
 * 番号が引けない。材料の用語（`subject`）と種別から組み直せば、用語の部分だけを用語詳細へ繋げる。
 * 用語か種別の決まっていないコンポーネント（骨格だけのもの）と、用語を返さない古い API の応答は、
 * 今の表示名のまま出す。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business コンポーネント名を、用語（用語詳細へのリンク）と種別の名前を訳の語順で並べて描き、材料が欠けていれば表示名のまま出す。
 * @graph-connects componentKindText [calls] 種別の名前
 * @graph-connects ElementTermLink [calls] 用語の部分を用語詳細へ繋ぐ
 * @graph-connects displayLabel [calls] 材料が欠けたコンポーネントの表示名
 */
export function ComponentName({ component, link }: ComponentNameProps) {
  const { t } = useTranslation();
  const subject = component.subject ?? null;
  const kind = componentKindText(component, t);
  if (subject === null || subject.label === '' || kind === null) {
    return <>{displayLabel(component.label, t, component.untranslated)}</>;
  }
  const [head = '', tail = ''] = t('screenDetail.componentName', {
    subject: SUBJECT_SLOT,
    kind,
  }).split(SUBJECT_SLOT);
  return (
    <span data-component-subject={subject.id}>
      {head}
      {link ? (
        <ElementTermLink id={subject.id} title={subject.label}>
          {subject.label}
        </ElementTermLink>
      ) : (
        subject.label
      )}
      {tail}
    </span>
  );
}

/**
 * 操作結果の 1 行。文字と、右パネルでリンクにする行き先（業務 ・ 業務ルール ・ 画面）を持つ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business コンポーネントの操作結果 1 行（文字と、業務 ・ 業務ルール ・ 画面のどれへ繋ぐか）
 * @graph-connects none
 */
export interface OperationResult {
  /** 行の同一性。 */
  readonly key: string;
  /** 「「〈業務名〉」を実行する」「「〈画面名〉」に遷移する」。 */
  readonly text: string;
  /** リンクの行き先の種類。 */
  readonly route: 'business' | 'rule' | 'screen';
  /** 行き先の番号。 */
  readonly id: number;
}

/**
 * コンポーネントの操作結果を、見本の画面仕様書の書き方（「「〈業務名〉」を実行する」「「〈画面名〉」に遷移する」）
 * の行にする。名前はかぎ括弧で囲む — 業務名は動詞で終わるので、囲まないと「〜するを実行する」になる
 * （2026-09-29 Dhik「謎日本語使うな」）。業務を操作し画面も移るコンポーネントは 2 行以上になる。どちらも無ければ空の並び。
 *
 * @param component コンポーネント 1 件
 * @param translate 表示言語への翻訳
 * @returns 操作結果の行（実行する業務、遷移する画面の順）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business コンポーネントの操作結果を「「〈業務名〉」を実行する」と「「〈画面名〉」に遷移する」の行にする。
 * @graph-connects displayLabel [calls] 業務 ・ 画面の表示名
 */
export function operationResultsOf(
  component: ScreenComponentRow,
  translate: ParamTranslate,
): readonly OperationResult[] {
  const { operates } = component;
  const run: OperationResult[] =
    operates === null
      ? []
      : [
          {
            key: `run-${String(operates.id)}`,
            text: translate('screenDetail.operationRun', {
              name: displayLabel(operates.label, translate),
            }),
            route: operates.nodeType === 'Business' ? 'business' : 'rule',
            id: operates.id,
          },
        ];
  const transitions = component.transitionTo.map(
    (target): OperationResult => ({
      key: `to-${String(target.id)}`,
      text: translate('screenDetail.operationTransition', {
        name: displayLabel(target.label, translate),
      }),
      route: 'screen',
      id: target.id,
    }),
  );
  return [...run, ...transitions];
}

/**
 * 操作結果の行を 1 行ずつ描く。1 行も無ければ「—」。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business コンポーネントの操作結果を 1 行ずつ描き、無ければ空であることだけを「—」で示す。
 * @graph-connects operationResultsOf [calls] 操作結果の行
 */
export function OperationResultLines({ component }: { component: ScreenComponentRow }) {
  const { t } = useTranslation();
  const results = operationResultsOf(component, t);
  if (results.length === 0) return <ValueOrDash value={null} />;
  return (
    <>
      {results.map((result) => (
        <span key={result.key} data-result={result.route} className="block">
          {result.text}
        </span>
      ))}
    </>
  );
}

/**
 * 値か、無ければ「—」を描く。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 欄の値を描き、値が無ければ空であることだけを「—」で示す。
 * @graph-connects none
 */
export function ValueOrDash({ value }: { value: string | null }) {
  if (value === null) return <span className="text-[var(--color-text-muted)]">{DASH}</span>;
  return <>{value}</>;
}

/** `ScreenComponentTable` の props。 @graph-connects none */
interface ScreenComponentTableProps {
  components: readonly ScreenComponentRow[];
  /** 右パネルで開いているコンポーネントの番号。開いていなければ null。 */
  selectedId: number | null;
  onSelect: (componentId: number) => void;
}

/**
 * コンポーネントの表（案件なし）。コンポーネント名 ／ 種別 ／ 操作結果 の 3 列で、値の無いセルは「—」だけ。
 *
 * 見本の画面仕様書の列のうち、コンポーネントどうしを見比べる 3 つだけを列にする。表示条件・変数部の
 * 表示ロジックと、操作結果のリンクは右パネルで読む。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面に載るコンポーネントを コンポーネント名 ／ 種別 ／ 操作結果 の 3 列の表に描き、行か先頭のコンポーネント名のボタン（Enter ・ Space でも）を押すと右パネルで開く。
 * @graph-connects componentKindText [calls] 種別
 * @graph-connects ComponentName [calls] コンポーネント名（ボタンの中なので用語はリンクにしない）
 * @graph-connects OperationResultLines [calls] 操作結果
 * @graph-connects selectableRowProps [calls] 行の押し方
 * @graph-connects RowOpenButton [calls] 行を開くボタン
 */
export function ScreenComponentTable({
  components,
  selectedId,
  onSelect,
}: ScreenComponentTableProps) {
  const { t } = useTranslation();
  return (
    <table className="w-full border-collapse text-sm" data-screen-components>
      <thead>
        <tr>
          <th className={`${HEAD_CELL_CLASS} w-[36%]`}>{t('screenDetail.componentColumn')}</th>
          <th className={`${HEAD_CELL_CLASS} w-24`}>{t('screenDetail.componentKind')}</th>
          <th className={HEAD_CELL_CLASS}>{t('screenDetail.componentResult')}</th>
        </tr>
      </thead>
      <tbody>
        {components.map((component) => {
          const selected = component.componentId === selectedId;
          return (
            <tr
              key={component.componentId}
              data-id={component.componentId}
              {...selectableRowProps(() => {
                onSelect(component.componentId);
              }, selected)}
            >
              <td className={CELL_CLASS}>
                <RowOpenButton expanded={selected}>
                  <ComponentName component={component} link={false} />
                </RowOpenButton>
              </td>
              <td className={CELL_CLASS}>
                <ValueOrDash value={componentKindText(component, t)} />
              </td>
              <td className={CELL_CLASS}>
                <OperationResultLines component={component} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
